<script setup lang="ts">
import type { Album } from "@/models"
import type { GuestbookMessage } from "@/models/message"
import { useQuery } from "@tanstack/vue-query"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, ref } from "vue"
import { listGuestbook, useGetEventVault, useListAlbums } from "@/api"
import {
  AlbumSelector,
  AlbumsLayout,
  FlagContentModal,
  Footer,
  GuestbookMessageDrawer,
  MainGalleryHiddenMessage,
  MessageDeleteModal,
  MessageEditModal,
  MessageTile,
} from "@/components"
import { Button } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useModal, useTranslation } from "@/composables"
import { GuestLayout } from "@/layouts"
import { EventVaultStatus } from "@/models"
import { GuestbookMessageType } from "@/models/message"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useTemplateStore } from "@/stores/templateStore"
import { FlagContentModalKey, GuestbookMessageDrawerKey, MessageDeleteModalKey, MessageEditModalKey } from "@/symbols"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "multi",
  },
})

const toast = useToast()

type MessageDrawerMode = "text" | "video"
const messageDrawerMode = ref<MessageDrawerMode>("text")

const { t } = useTranslation()
const { toggle: toggleDelete } = useModal(MessageDeleteModalKey)
const { toggle: toggleEdit } = useModal(MessageEditModalKey)
const { toggle: toggleFlagContentModal } = useModal(FlagContentModalKey)
const { toggle: toggleGuestbookMessageDrawer } = useModal(GuestbookMessageDrawerKey)

const eventVaultId = useConvertedRouteParam("id", "/guest/[id]/messages", value => Number(value))

const templateStore = useTemplateStore()
const eventVaultStore = useEventVaultStore()

const albumId = ref<number | undefined>()
const selectedMessage = ref<GuestbookMessage | undefined>()

const { data: eventVault, isLoading: isLoadingEventVault } = useGetEventVault(eventVaultStore.eventVaultId)
const { data: albums, isLoading: isLoadingAlbums } = useListAlbums(eventVaultStore.eventVaultId)

const { data: messages, isLoading: isLoadingMessages, isFetching: isFetchingMessages } = useQuery({
  queryKey: ["listMessages", eventVaultId.value, albumId],
  queryFn: () => listGuestbook(eventVaultStore.eventVaultId, albumId.value),
  initialData: [],
})

function onAlbumSelect(album: Album) {
  const params = useUrlSearchParams()
  if (album.id)
    params.album = album.id.toString()
  albumId.value = album.id
}

const isLoading = computed<boolean>(() => {
  return Boolean(isLoadingEventVault.value || isLoadingMessages.value || isFetchingMessages.value || isLoadingAlbums.value)
})

const isFeatureDisabled = computed(() => eventVault.value?.status === EventVaultStatus.Expired)

const isGalleryUnavailable = computed(
  () =>
    eventVault.value?.eventVaultSettings?.hideMainGallery
    && !albumId.value
    && !isLoadingEventVault.value
    && !eventVaultStore.isHost,
)

function showDeleteDialog(messageId: number) {
  selectedMessage.value = messages.value.find(message => message.id === messageId)
  toggleDelete()
}

function showEditDialog(message: GuestbookMessage) {
  selectedMessage.value = message
  toggleEdit()
}

function showFlagDialog(message: GuestbookMessage) {
  selectedMessage.value = message
  toggleFlagContentModal()
}

function showMessageDrawer(type: MessageDrawerMode) {
  messageDrawerMode.value = type
  toggleGuestbookMessageDrawer()
}

function onMessageDeleteSuccess() {
  toast.success({ title: t("success"), description: t("guestbook_messages_page.delete_success") })
}

function onMessageError() {
  toast.error()
}
</script>

<template>
  <GuestLayout hide-footer>
    <AlbumsLayout>
      <template #sidebar>
        <AlbumSelector
          v-if="eventVaultId && eventVault?.eventVaultSettings?.albumsEnabled"
          v-model="albumId"
          :event-vault-id="eventVaultId"
          :event-cover-image="eventVault?.coverImageUrl"
          :albums="albums"
          :hide-main-gallery="eventVault.eventVaultSettings?.hideMainGallery && !eventVaultStore.isHost"
          :is-loading="isLoadingAlbums"
          @select="onAlbumSelect"
        />
      </template>
      <MainGalleryHiddenMessage v-if="isGalleryUnavailable" :page-message="t('main_gallery_hidden_message.guestbook_message')" />
      <div v-else class="container mx-auto my-12 md:grid md:grid-cols-2">
        <div class="mb-10 md:sticky md:top-12 md:z-10 md:self-start md:bg-white md:pr-10">
          <h1 data-test="guestbook-messages-title" class="mb-8 font-comfortaa text-2xl font-bold text-primary">
            {{ t("guestbook_messages_page.title") }}
          </h1>
          <p data-test="guestbook-messages-intro" class="mb-8 font-montserrat font-normal">
            {{ t(`guestbook_messages_page.${eventVault?.eventVaultSettings?.shareGuestbookWithGuests ? "message" : "add_only_message"}`) }}
          </p>
          <div class="@container w-full">
            <div class="flex w-full flex-col items-stretch gap-2 @md:flex-row @md:flex-wrap @md:items-center">
              <div class="min-w-0 w-full @md:flex-1 @md:basis-[14rem]">
                <Button
                  data-test="guestbook-write-message-button"
                  class="w-full"
                  prefix-icon="Pen"
                  :disabled="isFeatureDisabled"
                  @click="showMessageDrawer('text')"
                >
                  {{ t("guestbook_messages_page.write_message_button") }}
                </Button>
              </div>
              <template v-if="eventVault?.eventVaultSettings?.allowVideoGuestbook && eventVault?.eventVaultSettings.videoEnabled">
                <span class="w-full shrink-0 text-center text-sm font-semibold text-gray-400 uppercase tracking-[0.2em] @md:w-auto @md:self-center">OR</span>
                <div class="min-w-0 w-full @md:flex-1 @md:basis-[14rem]">
                  <Button
                    data-test="guestbook-record-video-button"
                    class="w-full"
                    prefix-icon="Video"
                    :disabled="isFeatureDisabled"
                    @click="showMessageDrawer('video')"
                  >
                    {{ t("guestbook_messages_page.record_video_button") }}
                  </Button>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div>
          <div>
            <div
              v-if="isLoading"
              class="grid grid-cols-1 gap-4 [&>*]:border-t [&>*]:border-gray-300 md:[&>*]:border-t-0 md:[&>*:not(:first-child)]:border-t md:[&>*:not(:first-child)]:border-gray-300"
            >
              <MessageTile v-for="n in 5" :key="`${n}-loading`" is-loading />
            </div>
            <div v-else-if="messages?.length" class="grid grid-cols-1 gap-4">
              <div
                v-for="(message, index) in messages"
                :key="index"
                data-test="guestbook-message-tile"
                class="border-t border-gray-300 pt-8 md:border-t-0 md:pt-0 md:[&:not(:first-child)]:border-t md:[&:not(:first-child)]:border-gray-300 md:[&:not(:first-child)]:pt-8"
              >
                <MessageTile
                  v-if="message.type !== GuestbookMessageType.Unknown" :message="message"
                  :can-delete="(eventVaultStore.isHost || message.isCreator) && !isFeatureDisabled"
                  :can-edit="message.type === GuestbookMessageType.Text && message.isCreator && !isFeatureDisabled" @edit="showEditDialog"
                  @delete="showDeleteDialog"
                  @flag="showFlagDialog"
                />
              </div>
            </div>
            <div v-else>
              <p
                data-test="guestbook-messages-empty"
                class="mb-4 text-center font-montserrat font-medium text-primary md:text-start"
              >
                {{ t("guestbook_messages_page.empty_message") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer
        :custom-logo="templateStore.customFooterImage"
        :custom-footer-links="templateStore.customFooterLinks"
        :is-freemium-event="eventVault?.freemiumMode"
      />
    </AlbumsLayout>
  </GuestLayout>

  <MessageEditModal :message="selectedMessage" :event-vault-id="eventVaultId" />

  <MessageDeleteModal
    :message="selectedMessage"
    :event-vault-id="eventVaultId"
    @success="onMessageDeleteSuccess"
    @error="onMessageError"
  />

  <FlagContentModal
    v-if="selectedMessage"
    :event-vault-id="eventVaultId"
    :content="{ type: 'guestbookMessage', content: selectedMessage }"
  />

  <GuestbookMessageDrawer
    :mode="messageDrawerMode"
    :event-vault="eventVault"
    :event-vault-id="eventVaultId"
    :album-id="albumId"
    :is-event-vault-loading="isLoadingEventVault"
    :is-feature-disabled="isFeatureDisabled"
  />
</template>
